import { Injectable, effect, signal } from '@angular/core';
import { Option, fromNullable } from '../shared/functions/functional';
import { Theme } from './theme.service';

const STORAGE_KEYS = {
  browserUid: 'browserUid',
  dismissedMotdUid: 'dismissedMotdUid',
  emailToken: 'sid',
  recentProjects: 'recentProjects',
  theme: 'theme',
//  lastSignInDate: 'lastSignInDate',
} as const;

type StorageKey = typeof STORAGE_KEYS[keyof typeof STORAGE_KEYS];


@Injectable({
  providedIn: 'root',
})
export class LocalStorageService {

  /// Set up the signals with initial values from local storage (if present).
  readonly browserUid = signal(localStorage.getItem(STORAGE_KEYS.browserUid) ?? crypto.randomUUID());
  readonly dismissedMotdUid = signal(localStorage.getItem(STORAGE_KEYS.dismissedMotdUid) ?? '');
  readonly theme = signal<Theme>(localStorage.getItem(STORAGE_KEYS.theme) === 'dark' ? 'dark' : 'light');

  readonly recentProjects = signal<string[]>(
    JSON.parse(localStorage.getItem(STORAGE_KEYS.recentProjects) ?? '[]'));



  _setRecentProjects = effect(() => {
    localStorage.setItem(STORAGE_KEYS.recentProjects, JSON.stringify(this.recentProjects()));
  });

  _setBrowserUid = effect(() => { localStorage.setItem(STORAGE_KEYS.browserUid, this.browserUid()); });
  _setDismissedMotdUid = effect(() => {
    const dismissedMotdUid = this.dismissedMotdUid();
    if (dismissedMotdUid) {
      localStorage.setItem(STORAGE_KEYS.dismissedMotdUid, dismissedMotdUid);
      return;
    }

    localStorage.removeItem(STORAGE_KEYS.dismissedMotdUid);
  });
  _setTheme = effect(() => { localStorage.setItem(STORAGE_KEYS.theme, this.theme()); });

  getEmailSignInToken(): string | null {
    return localStorage.getItem(STORAGE_KEYS.emailToken);
  }

  setEmailSignInToken(emailToken: string): void {
    localStorage.setItem(STORAGE_KEYS.emailToken, emailToken);
  }

  clearEmailForSignIn(): void {
    localStorage.removeItem(STORAGE_KEYS.emailToken);
  }

  // Add a new project url to the start of the recent project list (new project index 0).
  // Keep to the maximum of 5 recent projects.
  recentProjectsAddProject(projectUid: string) {
    const recentProjects = this.recentProjects();
    const newRecentProjects = [projectUid, ...recentProjects.filter((x) => x !== projectUid)].slice(0, 5);
    this.recentProjects.set(newRecentProjects);
  }

  // Gets the first item of the recent projects list (last project added).
  recentProjectsGetLastProject(): Option<string> {
    return fromNullable(this.recentProjects()[0]);
  }

  setDismissedMotdUid(uid: string): void {
    this.dismissedMotdUid.set(uid);
  }

}
